import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { resolveNotificationUrl } from "@/lib/notificationUrl";

const priorityIcon: Record<string, string> = {
  urgent: "🔴",
  high: "🟡",
  normal: "🔵",
  low: "⚪",
};

const filterTabs = [
  { value: "all", label: "All" },
  { value: "unread", label: "Your Court 🟡" },
  { value: "waiting", label: "Waiting 🔵" },
  { value: "overdue", label: "Overdue 🔴" },
  { value: "actioned", label: "Completed ✅" },
];

const filterNotifs = (notifs: Notification[], tab: string) => {
  const now = new Date();
  switch (tab) {
    case "unread": return notifs.filter((n) => n.status === "unread");
    case "waiting": return notifs.filter((n) => n.type === "waiting" || n.type === "ball_in_court");
    case "overdue": return notifs.filter((n) => n.due_date && new Date(n.due_date) < now && n.status !== "actioned");
    case "actioned": return notifs.filter((n) => n.status === "actioned" || n.status === "dismissed");
    default: return notifs;
  }
};

const Notifications = () => {
  const isMobile = useIsMobile();
  const { data: notifications = [], markRead, markAllRead, dismiss } = useNotifications();
  const navigate = useTabNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
        <Button variant="outline" size="sm" onClick={() => markAllRead.mutate()}>
          Mark all read
        </Button>
      </div>

      <Tabs defaultValue="all">
        <TabsList className={isMobile ? "w-full overflow-x-auto flex justify-start" : ""}>
          {filterTabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="shrink-0">{t.label}</TabsTrigger>
          ))}
        </TabsList>

        {filterTabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="space-y-2">
            {filterNotifs(notifications, tab.value).length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">✅ Nothing here</div>
            ) : (
              filterNotifs(notifications, tab.value).map((n) => (
                <Card
                  key={n.id}
                  className={cn("cursor-pointer", n.status === "unread" && "border-primary/30 bg-primary/5")}
                  onClick={() => {
                    if (n.status === "unread") markRead.mutate(n.id);
                    const url = resolveNotificationUrl(n);
                    if (url) navigate(url);
                  }}
                >
                  <CardContent className={cn("py-3 px-4", isMobile ? "space-y-2" : "flex items-start gap-3")}>
                    <span className="text-lg mt-0.5">{priorityIcon[n.priority] ?? "🔵"}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{n.title}</p>
                      {n.message && <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>}
                      {n.entity_label && <p className="text-xs text-muted-foreground">{n.entity_label}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        {n.due_date && ` · Due ${new Date(n.due_date).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className={cn("flex gap-2", isMobile ? "w-full" : "shrink-0")}>
                      {resolveNotificationUrl(n) && (
                        <Button size="sm" className={isMobile ? "flex-1" : ""} onClick={(e) => { e.stopPropagation(); if (n.status === "unread") markRead.mutate(n.id); navigate(resolveNotificationUrl(n)!); }}>
                          {n.action_label ?? "View"}
                        </Button>
                      )}
                      {n.status === "unread" && (
                        <Button variant="outline" size="sm" className={isMobile ? "flex-1" : ""} onClick={(e) => { e.stopPropagation(); markRead.mutate(n.id); }}>
                          Mark read
                        </Button>
                      )}
                      {n.status !== "dismissed" && (
                        <Button variant="ghost" size="sm" className={isMobile ? "flex-1" : ""} onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}>
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default Notifications;